import { Params } from "@angular/router";
import { ItemBillingFlag } from "@app/shared/filters/item-billing-flag";
import { ItemCompletedFlag } from "@app/shared/filters/item-completed-flag";
import { buildRangeQueryParams } from "@app/shared/time-range-picker/range-query-params";
import { TRange } from "@app/shared/time-range-picker/time-range-constants";

/** Legacy base64 blob key (stripped if present; no longer written). */
export const REPORTS_SHARE_FILTER_PARAM = "f";

/** Toggle keys written by share (non-default only). */
export const REPORTS_SHARE_TOGGLE_KEYS = ["billable", "completed", "amount", "noStartDate"] as const;

/** Entity / audit keys never written by share; stripped if present (legacy). */
export const REPORTS_SHARE_LEGACY_SELECTION_KEYS = [
	REPORTS_SHARE_FILTER_PARAM,
	"users",
	"groups",
	"projects",
	"clients",
	"tasks",
	"tags",
	"comegoType",
	"audit",
] as const;

/** Deep-link keys (e.g. contingent -> schedules); not part of share copy. */
export const REPORTS_DEEP_LINK_KEYS = ["user", "project"] as const;

/** Query keys that should be consumed then stripped from the URL. */
export const REPORTS_SHARE_FILTER_KEYS = [
	...REPORTS_SHARE_TOGGLE_KEYS,
	...REPORTS_SHARE_LEGACY_SELECTION_KEYS,
	...REPORTS_DEEP_LINK_KEYS,
] as const;

export type ReportsShareFilterSnapshot = {
	range: TRange;
	billable: ItemBillingFlag;
	completed: ItemCompletedFlag;
	amount: boolean;
	noStartDate: boolean;
};

export function hasReportsShareFilterParams(queryParams: Params | null | undefined): boolean {
	if (!queryParams) return false;
	return REPORTS_SHARE_FILTER_KEYS.some((key) => {
		const value = queryParams[key];
		return value != null && value !== "";
	});
}

export type ReportsDeepLinkPlan = {
	/** Query keys to strip from the URL after handling. */
	stripKeys: string[];
	/** Keys marked consumed so later range-only navigations cannot re-apply them. */
	consumedKeys: string[];
	/** Filter mutations to apply (omit = leave current filter state unchanged). */
	team?: string[];
	clearGroups?: boolean;
	project?: string[];
	/** Tasks belong to a project, so a project deep link invalidates the task selection. */
	clearTasks?: boolean;
	billable?: ItemBillingFlag;
	completed?: ItemCompletedFlag;
	amount?: boolean;
	noStartDate?: boolean;
};

export type PlanReportsDeepLinkOptions = {
	queryParams: Params;
	workspaceUserIds: ReadonlySet<string>;
	/** Workspace user ids when resolving `user=all&project=...` (not limited to current project members). */
	projectUserIds?: string[];
	/** Keys already applied earlier this session (prevents re-apply on range URL sync). */
	consumedKeys?: ReadonlySet<string>;
};

function queryParamPresent(queryParams: Params, key: string): boolean {
	const value = queryParams?.[key];
	return value != null && value !== "";
}

/**
 * Plan share/deep-link filter apply + URL strip.
 * Range-only query changes must yield an empty plan (no filter mutations).
 * Each share/deep-link key is applied at most once per `consumedKeys` set.
 */
export function planReportsDeepLinkConsume(options: PlanReportsDeepLinkOptions): ReportsDeepLinkPlan {
	const { queryParams, workspaceUserIds, projectUserIds = [] } = options;
	const alreadyConsumed = options.consumedKeys ?? new Set<string>();
	const stripKeys = new Set<string>();
	const newlyConsumed = new Set<string>();
	const plan: ReportsDeepLinkPlan = { stripKeys: [], consumedKeys: [] };

	if (!hasReportsShareFilterParams(queryParams)) {
		return plan;
	}

	const markStrip = (key: string) => {
		if (queryParamPresent(queryParams, key)) stripKeys.add(key);
	};

	const shouldApply = (key: string): boolean => {
		markStrip(key);
		if (alreadyConsumed.has(key) || newlyConsumed.has(key)) return false;
		newlyConsumed.add(key);
		return true;
	};

	for (const key of REPORTS_SHARE_LEGACY_SELECTION_KEYS) {
		markStrip(key);
	}

	if (queryParams?.user === "all" && queryParamPresent(queryParams, "project")) {
		const applyUser = shouldApply("user");
		const applyProject = shouldApply("project");
		if (applyUser || applyProject) {
			plan.project = [String(queryParams.project)];
			plan.team = [...projectUserIds];
			plan.clearGroups = true;
			plan.clearTasks = true;
		}
	} else if (
		queryParamPresent(queryParams, "user") &&
		queryParams.user !== "all" &&
		workspaceUserIds.has(String(queryParams.user))
	) {
		if (shouldApply("user")) {
			plan.team = [String(queryParams.user)];
		}
	} else if (queryParamPresent(queryParams, "project") && queryParams?.user !== "all") {
		if (shouldApply("project")) {
			plan.project = [String(queryParams.project)];
			plan.clearTasks = true;
		}
	}

	if (queryParamPresent(queryParams, "billable")) {
		const billable = Number(queryParams.billable);
		if (Number.isFinite(billable) && shouldApply("billable")) {
			plan.billable = billable as ItemBillingFlag;
		} else {
			markStrip("billable");
		}
	}

	if (queryParamPresent(queryParams, "completed")) {
		const completed = Number(queryParams.completed);
		if (Number.isFinite(completed) && shouldApply("completed")) {
			plan.completed = completed as ItemCompletedFlag;
		} else {
			markStrip("completed");
		}
	}

	if (queryParamPresent(queryParams, "amount")) {
		if (shouldApply("amount")) {
			plan.amount = queryParams.amount === "1" || queryParams.amount === "true";
		}
	}

	if (queryParamPresent(queryParams, "noStartDate")) {
		if (shouldApply("noStartDate")) {
			plan.noStartDate = queryParams.noStartDate === "1" || queryParams.noStartDate === "true";
		}
	}

	plan.stripKeys = Array.from(stripKeys);
	plan.consumedKeys = Array.from(newlyConsumed);
	return plan;
}

/** Share params: range + non-default toggles only (no user/project/task/client/tag/audit). */
export function buildReportsShareQueryParams(snapshot: ReportsShareFilterSnapshot): Params {
	const params: Params = { ...buildRangeQueryParams(snapshot.range) };
	if (snapshot.billable !== ItemBillingFlag.BOTH) params.billable = snapshot.billable;
	if (snapshot.completed !== ItemCompletedFlag.NOT_COMPLETED) params.completed = snapshot.completed;
	if (snapshot.amount) params.amount = "1";
	if (snapshot.noStartDate) params.noStartDate = "1";
	return params;
}

export function buildShareUrlFromParams(
	path: string,
	params: Params,
	origin: string = typeof location !== "undefined" ? location.origin : ""
): string {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value == null || value === "") return;
		search.set(key, String(value));
	});
	const query = search.toString();
	const safePath = path || "/dashboard";
	return `${origin}${safePath}${query ? `?${query}` : ""}`;
}
